import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as FiIcons from "react-icons/fi";
const {
  FiHeart,
  FiShield,
  FiZap,
  FiUsers,
  FiActivity,
  FiBrain,
  FiArrowRight,
  FiSearch,
  FiCpu,
  FiLock,
  FiGlobe,
  FiAlertOctagon,
  FiFileText,
  FiCoffee,
  FiCheck,
  FiX,
  FiMenu,
} = FiIcons;
import SafeIcon from "../common/SafeIcon";
import { cn } from "../lib/utils";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const Landing = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-brand-200 selection:text-brand-900 overflow-x-hidden text-gray-900">
      {/* Navbar */}
      <nav className="fixed w-full z-50 bg-white/80 backdrop-blur-xl border-b border-brand-100/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shadow-lg shadow-brand-200 group-hover:shadow-brand-300 transition-all duration-300">
                <SafeIcon icon={FiShield} className="text-white text-xl" />
              </div>
              <span className="text-2xl font-black tracking-tighter bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                iVivaCore
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-brand-50 transition-colors"
              >
                <SafeIcon icon={FiMenu} className="text-xl text-gray-900" />
              </button>
              <div className="hidden md:flex items-center space-x-8">
                <Link
                  to="/signin"
                  className="text-xs font-black uppercase tracking-widest text-gray-900 hover:text-brand-500 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="px-6 py-3 bg-brand-500 text-white rounded-xl text-xs font-black uppercase tracking-widest transform hover:scale-105 transition-all shadow-xl shadow-brand-200"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden fixed top-20 left-0 right-0 bg-white/95 backdrop-blur-xl border-b border-brand-100/50 shadow-lg z-40">
          <div className="px-6 py-6 space-y-4">
            <Link
              to="/signin"
              className="block text-sm font-black uppercase tracking-widest text-gray-900 hover:text-brand-500 transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="block px-6 py-3 bg-brand-500 text-white rounded-xl text-sm font-black uppercase tracking-widest text-center shadow-xl shadow-brand-200 hover:shadow-brand-300 transition-all"
              onClick={() => setIsMenuOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-gradient-to-br from-brand-100/40 to-indigo-100/40 rounded-full blur-3xl opacity-50" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto mb-20">
            <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
              <span className="inline-block py-2 px-4 rounded-xl bg-gray-900 text-white text-[10px] font-black uppercase tracking-[0.3em] mb-8">
                REVOLUTIONIZING HEALTH INTELLIGENCE
              </span>
              <h1 className="text-5xl md:text-8xl font-black tracking-tighter text-gray-900 mb-8 leading-[0.9]">
                Bridging Vitals & <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-500 via-brand-600 to-indigo-600">
                  Neuro Health.
                </span>
              </h1>
              <p className="text-xl text-gray-500 mb-12 leading-relaxed max-w-3xl mx-auto font-medium">
                The first AI-powered platform that transforms real-time
                physiological data into predictive mental-health intelligence.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <Link
                  to="/signup"
                  className="w-full sm:w-auto px-10 py-5 bg-brand-500 text-white rounded-[1.5rem] font-black text-xs uppercase tracking-widest shadow-2xl shadow-brand-200 hover:shadow-brand-300 transition-all active:scale-95 flex items-center justify-center group"
                >
                  Start Monitoring Now
                  <SafeIcon
                    icon={FiArrowRight}
                    className="ml-2 group-hover:translate-x-1 transition-transform"
                  />
                </Link>
                <button className="w-full sm:w-auto px-10 py-5 bg-white border-2 border-brand-100 text-gray-900 rounded-[1.5rem] font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center hover:bg-brand-50 shadow-sm">
                  <SafeIcon icon={FiSearch} className="mr-2 text-brand-500" />
                  Detect Health Issues Before They Happen
                </button>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {[
              {
                icon: FiHeart,
                label: "Heart Rate",
                value: "72 BPM",
                color: "text-rose-500",
                bg: "bg-rose-50",
                pulse: true,
              },
              {
                icon: FiBrain,
                label: "Neuro AI",
                value: "Stable",
                color: "text-brand-500",
                bg: "bg-brand-50",
              },
              {
                icon: FiActivity,
                label: "Stability",
                value: "94%",
                color: "text-indigo-500",
                bg: "bg-indigo-50",
              },
            ].map((stat, i) => (
              <div
                key={i}
                className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl flex items-center space-x-6"
              >
                <div
                  className={cn(
                    "w-14 h-14 rounded-2xl flex items-center justify-center",
                    stat.bg,
                    stat.color,
                    stat.pulse && "animate-pulse",
                  )}
                >
                  <SafeIcon icon={stat.icon} className="text-2xl" />
                </div>
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {stat.label}
                  </p>
                  <p className="text-2xl font-black text-gray-900">
                    {stat.value}
                  </p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-black text-gray-900 tracking-tight mb-4">
              Institutional Grade
            </h2>
            <p className="text-brand-600 font-bold uppercase tracking-widest text-xs">
              Real-Time Physiological Mesh
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: FiActivity,
                title: "Live Telemetry",
                desc: "Continuous tracking of ECG, BP, and Glucose with millisecond precision.",
              },
              {
                icon: FiBrain,
                title: "Neuro Analysis",
                desc: "Predictive modeling for mental health states using regional brain mapping.",
              },
              {
                icon: FiAlertOctagon,
                title: "Protocol 4422",
                desc: "Automated high-priority emergency dispatch for cardiac or neuro crisis.",
              },
              {
                icon: FiShield,
                title: "HIPAA Compliant",
                desc: "End-to-end AES-256 encryption for all biometric and clinical data.",
              },
              {
                icon: FiCpu,
                title: "Ensemble Learning",
                desc: "Federated model weights updated via clinician-validated ground truth.",
              },
              {
                icon: FiGlobe,
                title: "Device Mesh",
                desc: "Seamless integration with Apple, Garmin, Dexcom, and Oura ecosystems.",
              },
              {
                icon: FiFileText,
                title: "Labs & Diagnostics",
                desc: "Processes structured medical reports. Critical biomarkers like cortisol are flagged for early alerts.",
              },
              {
                icon: FiCoffee,
                title: "Nutrition Intelligence",
                desc: "Analyzes food images to extract content and modulate AI brain-health predictions.",
              },
            ].map((cap, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -10 }}
                className="bg-white p-10 rounded-[3rem] border border-gray-100 shadow-sm hover:shadow-2xl transition-all group"
              >
                <div className="w-14 h-14 rounded-2xl bg-gray-50 text-gray-900 flex items-center justify-center mb-8 group-hover:bg-brand-500 group-hover:text-white transition-all">
                  <SafeIcon icon={cap.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-4">
                  {cap.title}
                </h3>
                <p className="text-gray-500 leading-relaxed text-sm font-medium">
                  {cap.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stakeholders Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-black text-gray-900 tracking-tight mb-4">
              Precision for Every Stakeholder
            </h2>
            <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">
              Unified Care Ecosystem
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Patients",
                desc: "Understand your Brain State. Personalized AI interventions and a 24/7 safety net.",
                icon: FiHeart,
              },
              {
                title: "Clinicians",
                desc: "SHAP-driven diagnostics. Predictive triage and instant crisis alerting.",
                icon: FiActivity,
              },
              {
                title: "Caregivers",
                desc: "Qualitative logging, emergency overrides, and automated shift summaries.",
                icon: FiUsers,
              },
              {
                title: "Admins",
                desc: "System integrity, RBAC controls, and immutable institutional audit logging.",
                icon: FiLock,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100"
              >
                <SafeIcon
                  icon={item.icon}
                  className="text-3xl text-brand-500 mb-6"
                />
                <h4 className="text-xl font-black text-gray-900 mb-3">
                  {item.title}
                </h4>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-32 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(0,206,209,0.1),transparent)]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-black tracking-tight mb-4">
              Flexible Institutional Tiers
            </h2>
            <p className="text-brand-400 font-bold uppercase tracking-widest text-xs">
              Compare capabilities across iVivaCore deployments
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              {
                tier: "Basic",
                price: "€19.99",
                period: "/mo",
                desc: "For individuals and wellness users",
                features: [
                  "Vitals Monitoring",
                  "Preventive AI Alerts",
                  "24/7 Health Dashboard",
                ],
                cta: "Get Started",
              },
              {
                tier: "Clinical",
                price: "€60",
                period: "/mo",
                desc: "For patients under clinical oversight",
                features: [
                  "All Basic Features",
                  "RPM Integration",
                  "AI Alerts Shared with Clinician",
                  "CPT-Aligned Billing (99454, 99457)",
                ],
                cta: "Launch Clinic Node",
                highlight: true,
              },
              {
                tier: "Institutional",
                price: "€120",
                period: "/mo/user",
                desc: "For hospitals, insurers, and platforms",
                features: [
                  "All Clinical Features",
                  "FHIR/HL7 Integration",
                  "Custom Analytics Dashboards",
                  "Enterprise Security & Bulk Licensing",
                ],
                cta: "Contact Sales",
              },
            ].map((plan, i) => (
              <div
                key={i}
                className={cn(
                  "p-12 rounded-[3.5rem] border transition-all hover:scale-[1.02]",
                  plan.highlight
                    ? "bg-brand-500 border-brand-400 text-white shadow-2xl shadow-brand-500/20"
                    : "bg-white/5 border-white/10 text-white",
                )}
              >
                <h3 className="text-2xl font-black mb-2">{plan.tier}</h3>
                <p
                  className={cn(
                    "text-sm font-medium mb-8",
                    plan.highlight ? "text-brand-100" : "text-gray-400",
                  )}
                >
                  {plan.desc}
                </p>
                <div className="flex items-baseline mb-10">
                  <span className="text-5xl font-black">{plan.price}</span>
                  <span className="text-sm font-bold opacity-60 ml-2">
                    {plan.period}
                  </span>
                </div>
                <ul className="space-y-4 mb-12">
                  {plan.features.map((f, j) => (
                    <li
                      key={j}
                      className="flex items-start space-x-3 text-sm font-bold"
                    >
                      <SafeIcon
                        icon={FiCheck}
                        className={
                          plan.highlight
                            ? "text-white mt-1"
                            : "text-brand-500 mt-1"
                        }
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={cn(
                    "w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all",
                    plan.highlight
                      ? "bg-gray-900 text-white shadow-xl"
                      : "bg-white text-gray-900",
                  )}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-20 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-12">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiShield} className="text-brand-500 text-3xl" />
              <span className="text-2xl font-black tracking-tighter">
                iVivaCore
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-8 text-xs font-black uppercase tracking-widest text-gray-400">
              <Link
                to="/contact"
                className="hover:text-brand-500 transition-colors"
              >
                Contact
              </Link>
              <Link
                to="/privacy"
                className="hover:text-brand-500 transition-colors"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="hover:text-brand-500 transition-colors"
              >
                Terms
              </Link>
              <Link
                to="/careers"
                className="hover:text-brand-500 transition-colors"
              >
                Careers
              </Link>
            </div>
            <p className="text-gray-400 text-sm font-medium">
              © 2026 iVivaCore Institutional Health. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
